import { useState, useEffect, useCallback, useRef } from 'react';
import { useContent } from '../hooks/useContent';
import './Hero.css';

function Ticker({ items: tickerItems }) {
  const items = [...tickerItems, ...tickerItems];
  return (
    <div className="ticker-wrap">
      <div className="ticker-track">
        {items.map((t, i) => (
          <span key={i} className="ticker-item">
            <span className="ticker-dot" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  const { HERO_SLIDES, STATS, TICKER_ITEMS, ui } = useContent();
  const [cur, setCur] = useState(0);
  const [animDir, setAnimDir] = useState(1);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);
  const TOTAL = HERO_SLIDES.length;
  const INTERVAL = 6000;

  const goTo = useCallback((idx, dir) => {
    if (animating || idx === cur) return;
    setAnimDir(dir);
    setAnimating(true);
    setTimeout(() => {
      setCur(idx);
      setAnimating(false);
    }, 500);
  }, [animating, cur]);

  const next = useCallback(() => goTo((cur + 1) % TOTAL, 1), [goTo, cur, TOTAL]);
  const prev = useCallback(() => goTo((cur - 1 + TOTAL) % TOTAL, -1), [goTo, cur, TOTAL]);

  useEffect(() => {
    timerRef.current = setInterval(next, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const slide = HERO_SLIDES[cur];

  return (
    <>
      <Ticker items={TICKER_ITEMS} />
      <section className="hero" id="hero">
        <div className="hero-bg" />
        <div className="hero-lines" />

        <div className="hero-content">
          {/* LEFT */}
          <div className="hero-left">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              <span>{slide.badge}</span>
            </div>

            <h1 className={`hero-title ${animating ? (animDir > 0 ? 'slide-out-left' : 'slide-out-right') : 'slide-in'}`}>
              {slide.title.map((line, i) => (
                <span key={i} className={i === slide.highlight ? 'hero-em' : ''}>
                  {line}<br />
                </span>
              ))}
            </h1>

            <p className={`hero-subtitle ${animating ? 'fade-out' : 'fade-in'}`}>
              {slide.subtitle}
            </p>

            <div className="hero-cta">
              <a className="btn-primary" href={slide.cta1.href}>
                <ArrowIcon /> {slide.cta1.label}
              </a>
              <a className="btn-secondary" href={slide.cta2.href}>
                {slide.cta2.label}
              </a>
            </div>

            {/* SLIDER CONTROLS */}
            <div className="hero-nav">
              <div className="hero-dots">
                {HERO_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    className={`hero-dot ${i === cur ? 'active' : ''}`}
                    onClick={() => goTo(i, i > cur ? 1 : -1)}
                    aria-label={`Слайд ${i + 1}`}
                  />
                ))}
              </div>
              <div className="hero-counter">
                <span className="hero-counter-cur">{String(cur + 1).padStart(2, '0')}</span>
                {' / '}
                {String(TOTAL).padStart(2, '0')}
              </div>
              <div className="hero-arrows">
                <button className="hero-arrow" onClick={prev} aria-label="Назад">
                  <ChevronLeft />
                </button>
                <button className="hero-arrow" onClick={next} aria-label="Вперёд">
                  <ChevronRight />
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT - STATS */}
          <div className="hero-right">
            {STATS.map((s, i) => (
              <div key={i} className="stat-card" style={{ animationDelay: `${0.4 + i * 0.1}s` }}>
                <div className="stat-number">
                  {s.num}
                  {s.unit && <span className="stat-unit">{s.unit}</span>}
                </div>
                <div className="stat-label">{s.label}</div>
                {s.sub && <div className="stat-sub">{s.sub}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* SCROLL INDICATOR */}
        <div className="hero-scroll">
          <span>{ui.hero.scroll}</span>
          <div className="scroll-line" />
        </div>
      </section>
    </>
  );
}

function ArrowIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
}
function ChevronLeft() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>;
}
function ChevronRight() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 6l6 6-6 6"/></svg>;
}